import styled from '@emotion/styled';
import { Suspense } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import type { FreeLegacyTextbookSubtopicDetailViewQuery } from '__free_textbooks_generated__/FreeLegacyTextbookSubtopicDetailViewQuery.graphql';
import ErrorBoundaryWithRetry from 'ms-components/ErrorBoundaryWithRetry';
import ProblemPreview from 'ms-components/math/ProblemPreview';
import OpenGraphTag from 'ms-helpers/OpenGraphTag';
import PageDescription from 'ms-helpers/PageDescription';
import PageTitle from 'ms-helpers/PageTitle';
import { useSnowplow } from 'ms-helpers/Snowplow/useSnowplow';
import TwitterCardTag from 'ms-helpers/TwitterCardTag';
import { FreeTextbookSubtopicTabs } from 'ms-pages/FreeTextbooks/FreeTextbooksSubtopicView/FreeTextbookSubtopicTabs';
import { FreeTextbookWorksheet } from 'ms-pages/FreeTextbooks/FreeTextbooksSubtopicView/FreeTextbookWorksheet';
import { FreeTextbooksLesson } from 'ms-pages/FreeTextbooks/FreeTextbooksSubtopicView/FreeTextbooksLesson';
import { useMaybeTeacherContext } from 'ms-pages/Teacher/TeacherContext/useTeacherContext';
import MinorSpinner from 'ms-pages/Teacher/components/MinorSpinner';
import {
  validateLegacyTextbookSubtopicActiveTab,
  type LegacyTextbookSubtopicActiveTab,
} from 'ms-pages/Textbooks/components/SubtopicDetailView/helpers';
import ShareButton from 'ms-pages/Textbooks/components/SunflowerSubtopicDetailView/ShareButton';
import { font } from 'ms-styles/base';
import { colors } from 'ms-styles/colors';
import { BASE_UNIT } from 'ms-styles/theme/Numero';
import Separator from 'ms-ui-primitives/Separator';
import { HStack, HSpacer } from 'ms-ui-primitives/Stack';
import useQueryParam from 'ms-utils/hooks/useQueryParam';
import { assertUnreachable } from 'ms-utils/typescript-utils';

import { FreeTextbooksSubtopicViewQuestions } from '../FreeTextbooksSubtopicViewQuestions/FreeTextbooksSubtopicViewQuestions';
import SEO, { LOCALIZATION } from '../seo';

const MainContentWrapper = styled.div({
  background: colors.white,
  width: '100%',
  height: '100%',
});

const ContentWrapper = styled.div({
  // just a stopgap so that spinner/empty  don't render too close to the tabs
  // seems to give a bit of breath to the content too by the way
  marginTop: 10 * BASE_UNIT,
});

const Heading = styled.div({
  ...font.gilroyHeading,
  color: colors.cloudBurst,
});

type Props = {
  subtopicId: string;
  scrollingElement: HTMLElement | null | undefined;
};

export function FreeLegacyTextbookSubtopicDetailView({
  subtopicId,
  scrollingElement,
}: Props) {
  return (
    <ErrorBoundaryWithRetry name="FreeLegacyTextbookSubtopicDetailView">
      {({ fetchKey }) => (
        <Suspense fallback={<MinorSpinner />}>
          <FreeLegacyTextbookSubtopicDetailViewInner
            subtopicId={subtopicId}
            scrollingElement={scrollingElement}
            fetchKey={fetchKey}
          />
        </Suspense>
      )}
    </ErrorBoundaryWithRetry>
  );
}

function FreeLegacyTextbookSubtopicDetailViewInner({
  subtopicId,
  scrollingElement,
  fetchKey,
}: Props & { fetchKey: string }) {
  const [activeTab] = useQueryParam<LegacyTextbookSubtopicActiveTab>(
    'activeTab',
    'theory',
    validateLegacyTextbookSubtopicActiveTab,
  );

  const { trackStructEvent } = useSnowplow();

  const { schoolId } = useMaybeTeacherContext();
  const data = useLazyLoadQuery<FreeLegacyTextbookSubtopicDetailViewQuery>(
    graphql`
      query FreeLegacyTextbookSubtopicDetailViewQuery($subtopicId: ID!) {
        subtopic(id: $subtopicId) {
          title
          rslJsx {
            transpiledJsx
          }
          worksheetJsx {
            transpiledJsx
          }
          topic {
            syllabus {
              title
              curriculumLevels {
                id
                name
              }
              country {
                code
              }
            }
          }
          hasExercise
          hasWorksheet
          topic {
            title
            syllabus {
              curriculum {
                title
              }
              curriculumLevels {
                name
              }
            }
          }
        }
      }
    `,
    { subtopicId },
    {
      fetchKey,
      networkCacheConfig: {
        // @ts-expect-error TODO fix this once we upgrade relay and have sound types
        useCache: true,
        ttl: Infinity,
      },
    },
  );

  const { subtopic } = data;
  if (subtopic == null) return <>Subtopic was not found</>;

  const { worksheetJsx, hasExercise, hasWorksheet } = subtopic;
  const worksheet = worksheetJsx.transpiledJsx;

  const curriculumLevel =
    subtopic.topic.syllabus.curriculumLevels[
      subtopic.topic.syllabus.curriculumLevels.length - 1
    ];

  if (curriculumLevel === undefined) {
    return <>No curriculum level found</>;
  }

  const mathPluralization = LOCALIZATION.MATH(
    subtopic.topic.syllabus.country?.code,
  );

  return (
    <MainContentWrapper>
      <FreeTextbookSubtopicTabs
        subtopicId={subtopicId}
        hasExercise={hasExercise}
        hasWorksheet={hasWorksheet}
        hasTeacherNote={false}
        onSelectTab={tab => {
          const sharedEventData = {
            category: 'textbook',
            label: schoolId || '',
          } as const;

          switch (tab) {
            case 'theory':
              trackStructEvent({
                ...sharedEventData,
                action: 'selected_theory_tab',
              });
              break;
            case 'interactive':
              trackStructEvent({
                ...sharedEventData,
                action: 'selected_question_tab',
              });
              break;
            case 'worksheet':
              trackStructEvent({
                ...sharedEventData,
                action: 'selected_worksheet_tab',
              });
              break;
            case 'teacherNotes':
              trackStructEvent({
                ...sharedEventData,
                action: 'selected_teacher_notes_tab',
              });
              break;
            case null:
              break;
            default:
              // Similar to the issue documented in the <Switch> component in
              // FreeCoreTextbookSubtopicDetailView.tsx the FreeTextbookSubtopicTabs
              // has been implemented against the full types for the non-public
              // textbook. 'engageActivity' is not intended
              // TODO revisit how all the types are mis-modelled for the public
              // textbook and fix them up, then we can remove this error suppression
              // @ts-expect-error
              assertUnreachable(tab);
          }
        }}
      />
      <ContentWrapper>
        {worksheet != null && activeTab === 'worksheet' && (
          <>
            <PageTitle
              subtitles={SEO.SUBTOPIC_WORKSHEET.title(subtopic.title)}
            />
            <PageDescription
              description={SEO.SUBTOPIC_WORKSHEET.description(subtopic.title)}
            />
            <HStack center>
              <Heading>Worksheet</Heading>
              <HSpacer width={24} grow />
              <ShareButton
                title="Share worksheet link"
                eventToTrack={{
                  category: 'free_textbook',
                  action: 'clicked_share',
                  label: 'worksheet',
                }}
              />
            </HStack>
            <Separator size={5} />
            <FreeTextbookWorksheet jsxContent={worksheet} />
          </>
        )}

        {activeTab === 'interactive' && (
          <>
            <PageTitle
              separator=" | "
              subtitles={SEO.SUBTOPIC_PRACTICE.title(subtopic.title)}
            />
            <PageDescription
              description={SEO.SUBTOPIC_PRACTICE.description(subtopic.title)}
            />
            <FreeTextbooksSubtopicViewQuestions subtopicId={subtopicId} />
          </>
        )}

        {activeTab === 'theory' && (
          <>
            <PageTitle
              separator=" | "
              subtitles={SEO.SUBTOPIC_LESSON.title(
                subtopic.title,
                curriculumLevel.name,
                subtopic.topic.syllabus.title,
                mathPluralization,
              )}
            />
            <PageDescription
              description={SEO.SUBTOPIC_LESSON.description(
                subtopic.topic.syllabus.curriculum.title,
                subtopic.topic.syllabus.curriculumLevels
                  .slice(-1)
                  .map(level => level.name)
                  .join(),
                subtopic.topic.title,
                subtopic.title,
              )}
            />
            <OpenGraphTag
              subtitles={SEO.SUBTOPIC_LESSON.title(
                subtopic.title,
                curriculumLevel.name,
                subtopic.topic.syllabus.title,
                mathPluralization,
              )}
              images={SEO.SUBTOPIC_LESSON.images}
            />
            <TwitterCardTag card="summary_large_image" />
            <HStack center>
              <Heading>Lesson</Heading>
              <HSpacer width={24} grow />
              <ShareButton
                title="Share lesson link"
                eventToTrack={{
                  category: 'free_textbook',
                  action: 'clicked_share',
                  label: 'theory',
                }}
              />
            </HStack>
            <Separator size={5} />
            <FreeTextbooksLesson
              scrollingElement={scrollingElement}
              nodeRenderers={{
                PROBLEM_PREVIEW: props => (
                  <ProblemPreview {...{ ...props, hideProblemPreview: true }} />
                ),
              }}
              subtopicId={subtopicId}
            />
          </>
        )}
      </ContentWrapper>
    </MainContentWrapper>
  );
}
